import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useHullStabilityComparison } from '../hooks/useHullStabilityComparison';
import { useStrokeStabilityData } from '../hooks/useStrokeStabilityData';
import type { HullStabilityComparison } from '../utils/hullStability/filterStrokePowers';
import {
  buildAllowedStrokeIndicesFromStability,
  buildAllowedStrokeIndicesFromWindows,
} from '../utils/hullStability/strokeAlignment';
import type { StrokeStabilityRecord } from '../hooks/useStrokeStabilityData';
import type { SummaryMetrics } from '../hooks/useSummaryPerStrokeData';

type HullStabilityFilterContextValue = {
  setStrokesOnly: boolean;
  setSetStrokesOnly: (value: boolean) => void;
  /** True when toggle is on and scored strokes exist — charts should filter. */
  isFilterActive: boolean;
  hasData: boolean;
  isLoading: boolean;
  stabilityRows: StrokeStabilityRecord[];
  comparison: HullStabilityComparison | null;
  strokeMetricRows: Record<string, unknown>[];
  orderedStrokeTimestamps: number[];
  boatMotionMinTimestamp: number | null;
  allowedStrokeTimestamps: Set<number> | null;
  allowedStrokeIndices: Set<number> | null;
  activeMetrics: SummaryMetrics | null;
  refetchStability: () => Promise<unknown>;
};

const HullStabilityFilterContext = createContext<HullStabilityFilterContextValue | null>(null);

export function HullStabilityFilterProvider({
  sessionId,
  children,
}: {
  sessionId: string | null;
  children: React.ReactNode;
}) {
  const [setStrokesOnly, setSetStrokesOnly] = useState(false);
  const stabilityQuery = useStrokeStabilityData(sessionId);
  const comparisonQuery = useHullStabilityComparison(sessionId, stabilityQuery.data);

  const refetchStability = useCallback(async () => {
    // Comparison query closes over stability rows; refetch those first so the
    // query key (set count) updates before comparison re-runs.
    await stabilityQuery.refetch();
  }, [stabilityQuery]);

  const value = useMemo((): HullStabilityFilterContextValue => {
    const snapshot = comparisonQuery.data ?? null;
    const comparison = snapshot?.comparison ?? null;
    const strokeMetricRows = snapshot?.strokeMetricRows ?? [];
    const orderedStrokeTimestamps = snapshot?.orderedStrokeTimestamps ?? [];
    const boatMotionMinTimestamp = snapshot?.boatMotionMinTimestamp ?? null;
    const hasData = (stabilityQuery.data?.length ?? 0) > 0;
    const isFilterActive = setStrokesOnly && hasData && comparison != null;
    const allowedStrokeTimestamps = isFilterActive && comparison ? comparison.setTimestamps : null;
    const allowedStrokeIndices = (() => {
      if (!isFilterActive || (stabilityQuery.data?.length ?? 0) === 0) return null;

      const fromWindows = buildAllowedStrokeIndicesFromWindows(
        snapshot?.strokeWindows ?? [],
        orderedStrokeTimestamps,
        stabilityQuery.data ?? [],
        boatMotionMinTimestamp
      );

      if (fromWindows.size > 0) return fromWindows;

      return buildAllowedStrokeIndicesFromStability(
        orderedStrokeTimestamps,
        stabilityQuery.data ?? [],
        boatMotionMinTimestamp
      );
    })();
    const activeMetrics = comparison
      ? setStrokesOnly
        ? comparison.setMetrics
        : comparison.allMetrics
      : null;

    return {
      setStrokesOnly,
      setSetStrokesOnly,
      isFilterActive,
      hasData,
      isLoading: stabilityQuery.isLoading || comparisonQuery.isLoading,
      stabilityRows: stabilityQuery.data ?? [],
      comparison,
      strokeMetricRows,
      orderedStrokeTimestamps,
      boatMotionMinTimestamp,
      allowedStrokeTimestamps,
      allowedStrokeIndices,
      activeMetrics,
      refetchStability,
    };
  }, [setStrokesOnly, stabilityQuery, comparisonQuery, refetchStability]);

  return (
    <HullStabilityFilterContext.Provider value={value}>
      {children}
    </HullStabilityFilterContext.Provider>
  );
}

export function useHullStabilityFilter(): HullStabilityFilterContextValue {
  const ctx = useContext(HullStabilityFilterContext);
  if (!ctx) {
    throw new Error('useHullStabilityFilter must be used within HullStabilityFilterProvider');
  }
  return ctx;
}

/** Safe for chart components that may render outside the coach dashboard provider. */
export function useHullStabilityFilterOptional(): HullStabilityFilterContextValue | null {
  return useContext(HullStabilityFilterContext);
}

export function hullSetFilterSubtitle(
  baseSubtitle: string | undefined,
  filter: HullStabilityFilterContextValue | null
): string | undefined {
  if (!filter?.isFilterActive) return baseSubtitle;
  const setCount = filter.comparison?.setCount ?? 0;
  const suffix = `Set strokes only (${setCount} strokes)`;
  return baseSubtitle ? `${baseSubtitle} · ${suffix}` : suffix;
}
